import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const codeSnippet = `<header className="hero-section">
  <nav className="flex justify-between">
    <Logo />
    <Button variant="primary">
      Book Estimate
    </Button>
  </nav>
  
  <div className="content-wrapper">
    <h1>Phoenix's #1 HVAC Experts</h1>
    <p>Fast, reliable service 24/7.</p>
  </div>
</header>`;
export function CodeTypingEffect() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [isFinished, setIsFinished] = useState(false);
  // Typing effect — very fast, types 2-3 chars per tick, finishes in ~2 seconds
  useEffect(() => {
    if (isFinished) return;
    if (currentIndex < codeSnippet.length) {
      const timeout = setTimeout(() => {
        const charsToType = Math.floor(Math.random() * 2) + 2; // 2-3 chars per tick
        setCurrentIndex(prev => Math.min(prev + charsToType, codeSnippet.length));
      }, 12);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setIsFinished(true);
      }, 400);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, isFinished]);
  // Blinking cursor
  useEffect(() => {
    if (isFinished) return;
    const interval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(interval);
  }, [isFinished]);
  const currentText = codeSnippet.substring(0, currentIndex);
  const highlightCode = (code: string) => {
    let html = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    html = html.replace(/(".*?"|"[^"]*$)/g, '<span class="text-green-300">$1</span>');
    html = html.replace(/&lt;(\/?)([a-zA-Z0-9]+)/g, '&lt;$1<span class="text-pink-400">$2</span>');
    html = html.replace(/([a-zA-Z\-]+)=/g, '<span class="text-blue-300">$1</span>=');
    html = html.replace(/<span class="text-pink-400">([A-Z][a-zA-Z0-9]*)<\/span>/g, '<span class="text-amber-300">$1</span>');
    return html;
  };
  const lineCount = codeSnippet.split('\n').length;
  return <div className="h-[350px] relative overflow-hidden" data-id="element-104">
      <AnimatePresence mode="wait" data-id="element-105">
        {!isFinished ? <motion.div key="code" initial={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 0.98
      }} transition={{
        duration: 0.4
      }} className="bg-[#0d1117] h-full flex font-mono text-sm sm:text-base" data-id="element-106">
            {/* Line Numbers */}
            <div className="flex flex-col text-gray-600 text-right pr-4 pl-4 py-6 select-none border-r border-gray-800/50" data-id="element-107">
              {Array.from({
            length: lineCount
          }).map((_, i) => <span key={i} className="leading-relaxed" data-id="element-108">
                  {i + 1}
                </span>)}
            </div>

            {/* Code Area */}
            <div className="flex-1 p-6 overflow-hidden" data-id="element-109">
              <pre className="text-gray-300 whitespace-pre-wrap break-words m-0 leading-relaxed" data-id="element-110">
                <code dangerouslySetInnerHTML={{
              __html: highlightCode(currentText)
            }} data-id="element-111" />
                <span className={`inline-block w-2.5 h-5 bg-viridia align-middle ml-[1px] -mt-1 transition-opacity duration-100 ${showCursor ? 'opacity-100' : 'opacity-0'}`} data-id="element-112" />
              </pre>
            </div>
          </motion.div> : <motion.div key="site" initial={{
        opacity: 0,
        scale: 1.02
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="bg-white h-full flex flex-col" data-id="element-113">
            {/* Rendered "Finished Website" — distinct blue/orange theme */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-blue-100 bg-blue-50/50" data-id="element-114">
              <div className="flex items-center gap-2" data-id="element-115">
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center" data-id="element-116">
                  <span className="text-white font-bold text-xs" data-id="element-117">AC</span>
                </div>
                <span className="font-bold text-gray-900 text-sm" data-id="element-118">
                  AirComfort Pro
                </span>
              </div>
              <div className="flex items-center gap-4" data-id="element-119">
                <span className="text-gray-500 text-xs hidden sm:block" data-id="element-120">
                  Services
                </span>
                <span className="text-gray-500 text-xs hidden sm:block" data-id="element-121">
                  Reviews
                </span>
                <span className="text-gray-500 text-xs hidden sm:block" data-id="element-122">
                  About
                </span>
                <div className="bg-orange-500 text-white text-xs font-semibold px-4 py-2 rounded-lg" data-id="element-123">
                  Book Estimate
                </div>
              </div>
            </div>

            <div className="flex-1 bg-gradient-to-br from-blue-950 to-slate-900 px-6 py-8 flex flex-col justify-center relative overflow-hidden" data-id="element-124">
              <div className="absolute top-4 right-4 w-24 h-24 rounded-full bg-blue-500/15 blur-2xl" data-id="element-125"></div>
              <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.4
          }} data-id="element-126">
                <div className="inline-flex items-center gap-1.5 bg-orange-500/10 border border-orange-500/20 rounded-full px-3 py-1 mb-4" data-id="element-127">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" data-id="element-128"></div>
                  <span className="text-orange-400 text-[10px] font-semibold" data-id="element-129">
                    SERVING PHOENIX 24/7
                  </span>
                </div>
                <h2 className="text-white text-2xl font-extrabold leading-tight mb-2" data-id="element-130">
                  Phoenix's #1
                  <br data-id="element-131" />
                  HVAC Experts
                </h2>
                <p className="text-gray-400 text-xs mb-5 max-w-[200px]" data-id="element-132">
                  Fast, reliable service. Licensed & insured. Same-day
                  appointments available.
                </p>
                <div className="flex gap-2" data-id="element-133">
                  <div className="bg-orange-500 text-white text-xs font-bold px-4 py-2.5 rounded-lg shadow-lg shadow-orange-500/20" data-id="element-134">
                    Book Now →
                  </div>
                  <div className="border border-blue-400/30 text-blue-200 text-xs font-medium px-4 py-2.5 rounded-lg" data-id="element-135">
                    (602) 555-0199
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t border-gray-100" data-id="element-136">
              <div className="flex items-center gap-3" data-id="element-137">
                <div className="flex -space-x-1" data-id="element-138">
                  {[0, 1, 2].map(i => <div key={i} className="w-5 h-5 rounded-full bg-gray-300 border-2 border-white" data-id="element-139" />)}
                </div>
                <span className="text-[10px] text-gray-500 font-medium" data-id="element-140">
                  127 five-star reviews
                </span>
              </div>
              <div className="flex gap-0.5" data-id="element-141">
                {[0, 1, 2, 3, 4].map(i => <svg key={i} className="w-3 h-3 text-amber-400 fill-current" viewBox="0 0 20 20" data-id="element-142">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-143" />
                  </svg>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}